import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  CreatePriceApprovalRangeDto,
  PriceApprovalRange,
  PriceApprovalRangePagedResult,
  UpdatePriceApprovalRangeDto
} from '../models/PriceApprovalRange';
import { ErrorService } from '../shared/services/error.service';

@Injectable({ providedIn: 'root' })
export class PriceApprovalRangesService {
  private readonly baseUrl = environment.microservices.shopping;

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  create(body: CreatePriceApprovalRangeDto): Observable<PriceApprovalRange> {
    return this.http.post<any>(`${this.baseUrl}/price-approval-ranges`, body).pipe(
      map((resp) => this.mapItem(resp?.data ?? resp)),
      catchError((err) => {
        this.errorService.handleHttpError(err, 'Erro ao criar faixa de aprovação');
        return throwError(() => err);
      })
    );
  }

  getById(ruleId: number): Observable<PriceApprovalRange> {
    return this.http.get<any>(`${this.baseUrl}/price-approval-ranges/${ruleId}`).pipe(
      map((resp) => this.mapItem(resp?.data ?? resp)),
      catchError((err) => {
        this.errorService.handleHttpError(err, 'Erro ao carregar faixa de aprovação');
        return throwError(() => err);
      })
    );
  }

  update(ruleId: number, body: UpdatePriceApprovalRangeDto): Observable<PriceApprovalRange> {
    return this.http.put<any>(`${this.baseUrl}/price-approval-ranges/${ruleId}`, body).pipe(
      map((resp) => this.mapItem(resp?.data ?? resp)),
      catchError((err) => {
        this.errorService.handleHttpError(err, 'Erro ao atualizar faixa de aprovação');
        return throwError(() => err);
      })
    );
  }

  getPaged(page = 1, pageSize = 10): Observable<PriceApprovalRangePagedResult> {
    const params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize));
    return this.http.get<any>(`${this.baseUrl}/price-approval-ranges`, { params }).pipe(
      map((resp) => this.mapPaged(resp)),
      catchError((err) => {
        this.errorService.handleHttpError(err, 'Erro ao carregar faixas de aprovação');
        return throwError(() => err);
      })
    );
  }

  private mapPaged(resp: any): PriceApprovalRangePagedResult {
    const rawItems =
      (Array.isArray(resp?.items) && resp.items) ||
      (Array.isArray(resp?.Items) && resp.Items) ||
      (Array.isArray(resp?.data?.items) && resp.data.items) ||
      [];
    const items = rawItems.map((r: any) => this.mapItem(r));
    const page = Number(resp?.page ?? resp?.Page ?? 1);
    const pageSize = Number(resp?.pageSize ?? resp?.PageSize ?? 10);
    const totalItems = Number(resp?.totalItems ?? resp?.TotalItems ?? items.length);
    const totalPages = Number(
      resp?.totalPages ?? resp?.TotalPages ?? (pageSize > 0 ? Math.ceil(totalItems / pageSize) : 1)
    );
    return { items, page, pageSize, totalItems, totalPages };
  }

  private mapItem(raw: any): PriceApprovalRange {
    const approversRaw = raw?.additionalApprovers ?? raw?.AdditionalApprovers ?? [];
    const additionalApprovers = Array.isArray(approversRaw)
      ? approversRaw.map((a: any) => ({
          approvalLevel: a?.approvalLevel ?? a?.ApprovalLevel,
          staffId: Number(a?.staffId ?? a?.StaffId ?? 0) || undefined,
          staffName: a?.staffName ?? a?.StaffName,
          responsibleStaffId: Number(a?.responsibleStaffId ?? a?.ResponsibleStaffId ?? 0) || undefined,
          responsibleStaffName: a?.responsibleStaffName ?? a?.ResponsibleStaffName,
          approvalOrder: Number(a?.approvalOrder ?? a?.ApprovalOrder ?? 0) || undefined,
          isRequired: Boolean(a?.isRequired ?? a?.IsRequired),
          isActive: Boolean(a?.isActive ?? a?.IsActive ?? true)
        }))
      : [];
    return {
      id: Number(raw?.id ?? raw?.Id ?? 0),
      ruleName: String(raw?.ruleName ?? raw?.RuleName ?? ''),
      minAmount: Number(raw?.minAmount ?? raw?.MinAmount ?? 0),
      maxAmount: Number(raw?.maxAmount ?? raw?.MaxAmount ?? 0),
      requiresManagerApproval: Boolean(raw?.requiresManagerApproval ?? raw?.RequiresManagerApproval),
      requiredAdditionalApprovalsCount: Number(
        raw?.requiredAdditionalApprovalsCount ?? raw?.RequiredAdditionalApprovalsCount ?? 0
      ),
      skipApprovalWhenInRange: Boolean(raw?.skipApprovalWhenInRange ?? raw?.SkipApprovalWhenInRange),
      requiresSequentialApproval: Boolean(
        raw?.requiresSequentialApproval ?? raw?.RequiresSequentialApproval
      ),
      isActive: Boolean(raw?.isActive ?? raw?.IsActive ?? true),
      description: raw?.description ?? raw?.Description,
      additionalApprovers,
      createdAt: raw?.createdAt ?? raw?.CreatedAt
    };
  }
}
